import { Box, IconButton, Paper, Stack, Typography, useTheme } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import { useEffect, useState } from "react";
import Button from "../../../../../../../components/common/Button";
import { ProposalService } from "./hooks/UseServices";
import AddServiceDialog from "./AddServiceDialog";
import { tokens } from "../../../../../../../theme";

interface IncludedServicesSectionProps {
  onServicesChange?: (services: ProposalService[]) => void;
  defaultServices?: ProposalService[];
}

export default function IncludedServicesSection({
  onServicesChange,
  defaultServices,
}: IncludedServicesSectionProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [services, setServices] = useState<ProposalService[]>(
    defaultServices ?? [],
  );

  useEffect(() => {
    onServicesChange?.(services);
  }, [services, onServicesChange]);

  const handleAdd = (ps: ProposalService) => {
    setServices((prev) => [...prev, ps]);
  };

  const handleRemove = (index: number) => {
    setServices((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Paper sx={{ borderRadius: "14px", p: 2.5, boxShadow: 0, mb: 2 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "15px", color: colors.primary[100] }}
        >
          Included Services
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setDialogOpen(true)}
          sx={{
            border: `1px solid ${colors.primary[500]}`,
            borderRadius: "20px",
            px: 2,
            py: 0.5,
            textTransform: "none",
            fontSize: "12px",
            color: colors.primary[500],
            fontWeight: 500,
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <AddRounded sx={{ fontSize: 14, mr: 0.5 }} />
          Add Additional Services
        </Button>
      </Stack>

      {services.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            bgcolor: "background.paper",
            borderRadius: "10px",
          }}
        >
          <Typography sx={{ fontSize: "12px", color: colors.primary[100] }}>
            No services added yet. Click {"Add Additional Services"} to get
            started.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {services.map((ps, index) => (
            <Box
              key={ps.service.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "background.paper",
                borderRadius: "10px",
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={{ fontSize: "12px", color: colors.primary[100], flex: 1 }}>
                {ps.service.name}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 700, color: colors.primary[100] }}
                >
                  ${ps.service.price.toLocaleString()}
                </Typography>
                <IconButton
                  onClick={() => handleRemove(index)}
                  sx={{
                    color: "#ef4444",
                    p: 0.5,
                    "&:hover": { bgcolor: colors.primary[800] },
                  }}
                >
                  <DeleteRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <AddServiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={handleAdd}
      />
    </Paper>
  );
}
